import { faArrowRightArrowLeft } from "@fortawesome/free-solid-svg-icons/faArrowRightArrowLeft";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { AtiraIcon } from "../../../components/AtiraIcon";
import { AtiraSelect } from "../../../components/AtiraSelect";
import { Button } from "../../../components/Button";
import { Flex } from "../../../components/Flex";
import { Text } from "../../../components/Text";
import { Modal } from "../../../components/modal/Modal";
import { PageMeta } from "../../../model/meta/PageMeta";
import { entryActions } from "../../../redux/entry/entry.slice";
import { useAppDispatch, useAppSelector } from "../../../redux/store";
import { userSliceSelectors } from "../../../redux/user/user.selector";
import { AtiraToast } from "../../../utils/AtiraToast";
import { useDrawersContext } from "../../tasks/drawers-context";
import type { UploadFile } from "../../../utils/local-antd";
import { Info } from "../../../components/Info";
import isEmpty from "lodash/isEmpty";
import { getTranslatedTableColumnLabel } from "../utils/getTranslatedTableColumnLabel";
import { entrySliceSelectors } from "../../../redux/entry/entry.selector";
import { EntriesTableMainFields } from "../../../model/entries-table-settings/types/EntriesTableMainFields.enum";
import { TestIDs } from "../../../tests/types/testIds";

type EntrieImportTabConflictsProps = {
  open: boolean;
  onClose: VoidFunction;
  userHeaders: string[];
  standardHeaders: string[];
  originalFile: UploadFile;
};

export const EntriesImportTabConflicts: React.FC<
  EntrieImportTabConflictsProps
> = ({ open, onClose, userHeaders, standardHeaders, originalFile }) => {
  const [loading, setLoading] = useState(false);
  const [resolvedHeaders, setResolvedHeaders] = useState<
    { standardHeader: string; userHeader: string }[]
  >([]);

  const userId = useAppSelector(userSliceSelectors.selectLoggedInUserId)!;
  const tableSettings = useAppSelector(
    entrySliceSelectors.selectUserEntryTableSettings,
  );
  const tableColumns = tableSettings?.columns || [];

  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  const { setImportExportVisible } = useDrawersContext();

  const onLocalClose = () => {
    onClose();
  };

  const onImportWithoutConflicts = async () => {
    try {
      const mappedResolvedObject = resolvedHeaders.reduce(
        (acc, { standardHeader, userHeader }) => {
          if (standardHeader && userHeader) {
            (acc as any)[standardHeader] = userHeader;
          }
          return acc;
        },
        {},
      );

      setLoading(true);

      await dispatch(
        entryActions.importEntries({
          userId,
          file: originalFile,
          headers: mappedResolvedObject,
        }),
      ).unwrap();

      AtiraToast.success(t("entries.import.conflict-modal.success"));
      dispatch(
        entryActions.getLatestEntries({ userId, meta: PageMeta.create() }),
      );
      onClose();
      setImportExportVisible(false);
    } catch (e: any) {
      console.log(e);
      AtiraToast.apiError(e, { duration: 10 });
    } finally {
      setLoading(false);
    }
  };

  const isContinueButtonDisabled = (): boolean => {
    const mainFields = tableSettings?.mainFields || [
      EntriesTableMainFields.EMAIL,
    ];

    return mainFields.some((mainField) => {
      const standardMapped = resolvedHeaders.find(
        (mapping) => mapping.standardHeader === mainField,
      );

      if (!standardMapped || !standardMapped.userHeader) {
        return true;
      }

      return false;
    });
  };

  useEffect(() => {
    setResolvedHeaders(
      Array.from({ length: userHeaders.length }, (v, i) => ({
        standardHeader: "",
        userHeader: userHeaders[i],
      })),
    );
  }, [standardHeaders, userHeaders]);

  return (
    <Modal
      destroyOnClose
      open={open}
      onClose={onLocalClose}
      title={t("entries.import.conflict-modal.title")}
    >
      <Flex flexDirection="column" gap="m" alignItems="center">
        <Info
          info={
            <Text style={{ whiteSpace: "pre-line" }}>
              {t("entries.import.conflict-modal.description-1")}
            </Text>
          }
        />

        <Info
          color="danger"
          info={t("entries.import.conflict-modal.required-fields")}
        />

        {userHeaders.length > standardHeaders.length ? (
          <Info
            color="warning"
            info={t("entries.import.conflict-modal.extra-columns", {
              fileColumns: userHeaders.length,
              supportedFields: standardHeaders.length,
            })}
          />
        ) : null}

        <Flex gap="xl" alignItems="flex-start" flexDirection="column">
          {resolvedHeaders.map((mapping, index) => (
            <Flex key={index} flexDirection="column" gap="xs">
              <Flex gap="s" alignItems="center">
                <AtiraSelect
                  className={TestIDs.entries.import.conflicts.standardSelect(
                    index,
                  )}
                  containerStyle={{ width: "10rem" }}
                  options={standardHeaders
                    .filter(
                      (h) =>
                        !resolvedHeaders.some(
                          (m, i) => m.standardHeader === h && i !== index,
                        ),
                    )
                    .map((h) => {
                      const column = tableColumns?.find((c) => c.field === h);

                      const label = !isEmpty(column)
                        ? getTranslatedTableColumnLabel(column)
                        : h;

                      return {
                        label,
                        value: h,
                        "data-testid":
                          TestIDs.entries.import.conflicts.standardOption(h),
                      };
                    })}
                  placeholder={t(
                    "entries.import.conflict-modal.select.standard.placeholder",
                  )}
                  onChange={(value) => {
                    setResolvedHeaders((prev) => {
                      const newMappings = [...prev];
                      newMappings[index] = {
                        ...mapping,
                        standardHeader: value,
                      };
                      return newMappings;
                    });
                  }}
                  value={mapping.standardHeader || undefined}
                  allowClear
                  onClear={() => {
                    setResolvedHeaders((prev) => {
                      const newMappings = [...prev];
                      newMappings[index] = { ...mapping, standardHeader: "" };
                      return newMappings;
                    });
                  }}
                />

                <AtiraIcon icon={faArrowRightArrowLeft} />

                <AtiraSelect
                  containerStyle={{ width: "10rem" }}
                  options={userHeaders
                    .filter(
                      (h) =>
                        !resolvedHeaders.some(
                          (m, i) => m.userHeader === h && i !== index,
                        ),
                    )
                    .map((h) => ({
                      label: h,
                      value: h,
                    }))}
                  placeholder={t(
                    "entries.import.conflict-modal.select.user.placeholder",
                  )}
                  onChange={(value) => {
                    setResolvedHeaders((prev) => {
                      const newMappings = [...prev];
                      newMappings[index] = { ...mapping, userHeader: value };
                      return newMappings;
                    });
                  }}
                  value={mapping.userHeader || undefined}
                  allowClear
                  onClear={() => {
                    setResolvedHeaders((prev) => {
                      const newMappings = [...prev];
                      newMappings[index] = { ...mapping, userHeader: "" };
                      return newMappings;
                    });
                  }}
                />
              </Flex>

              {mapping.userHeader && !mapping.standardHeader ? (
                <Text fontSize="xs" color="warning">
                  {t("entries.import.conflict-modal.column-skipped")}
                </Text>
              ) : null}
            </Flex>
          ))}
        </Flex>

        <Button
          onClick={onImportWithoutConflicts}
          title={t("entries.import.conflict-modal.button.title")}
          loading={loading}
          disabled={isContinueButtonDisabled()}
          margin="none"
          dataTestId={TestIDs.entries.import.conflicts.continueButton}
        />
      </Flex>
    </Modal>
  );
};
